import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Phone, IdCard, Mail, CheckCircle2 } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpStage, setOtpStage] = useState("start"); // start | check
  const [otpBusy, setOtpBusy] = useState(false);

  useEffect(() => { if (user) { setForm(user); setOtpPhone(user.phone || "+91"); } }, [user]);

  if (!user) return <><Navbar /><div className="max-w-3xl mx-auto p-8">Please log in</div></>;

  const save = async () => {
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Profile saved");
    } catch { toast.error("Failed"); }
  };

  const startOtp = async () => {
    if (!/^\+\d{8,15}$/.test(otpPhone)) { toast.error("Use E.164 format e.g. +919812345678"); return; }
    setOtpBusy(true);
    try {
      const r = await api.post("/profile/verify/phone/start", { phone: otpPhone });
      if (r.data.mode === "demo" && r.data.demo_code) {
        toast.success(`Demo code: ${r.data.demo_code}`, { duration: 15000 });
      } else {
        toast.success("OTP sent via SMS");
      }
      setOtpStage("check");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP");
    } finally { setOtpBusy(false); }
  };

  const checkOtp = async () => {
    setOtpBusy(true);
    try {
      await api.post("/profile/verify/phone/check", { phone: otpPhone, code: otpCode });
      toast.success("Phone verified!");
      setOtpOpen(false); setOtpStage("start"); setOtpCode("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setOtpBusy(false); }
  };

  const verifyId = async () => {
    await api.post(`/profile/verify/id`);
    await refresh();
    toast.success("ID verified (mock)");
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-medium">Your profile</h1>
        <p className="text-stone-600 mt-1">Better profiles get more accepts.</p>

        <section className="mt-8 p-6 bg-white border border-stone-200 rounded-2xl">
          <h2 className="font-display text-lg font-medium mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand" /> Verifications</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <VerifyRow icon={Mail} label="Email" done={user.verified_email} onVerify={null} />
            <div className="p-4 border border-stone-200 rounded-xl flex items-center gap-3">
              <Phone className="w-5 h-5 text-brand" />
              <div className="flex-1">
                <div className="text-sm font-medium">Phone</div>
                <div className="text-xs text-stone-500">{user.verified_phone ? user.phone || "Verified" : "Not verified"}</div>
              </div>
              {user.verified_phone ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> :
                <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="verify-phone" className="rounded-full">Verify</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Verify your phone</DialogTitle></DialogHeader>
                    {otpStage === "start" ? (
                      <>
                        <Label className="text-xs">Phone (E.164)</Label>
                        <Input data-testid="otp-phone" value={otpPhone} onChange={(e) => setOtpPhone(e.target.value)} placeholder="+919812345678" />
                        <p className="text-xs text-stone-500">We'll send a one-time code. Without SMS configured, a demo code appears in the toast.</p>
                        <DialogFooter>
                          <Button data-testid="otp-send" className="brand-accent rounded-full" onClick={startOtp} disabled={otpBusy}>
                            {otpBusy ? "Sending…" : "Send OTP"}
                          </Button>
                        </DialogFooter>
                      </>
                    ) : (
                      <>
                        <Label className="text-xs">Enter the 6-digit code sent to {otpPhone}</Label>
                        <Input data-testid="otp-code" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="123456" />
                        <DialogFooter>
                          <Button variant="ghost" onClick={() => setOtpStage("start")}>Change number</Button>
                          <Button data-testid="otp-check" className="brand-primary rounded-full" onClick={checkOtp} disabled={otpBusy}>
                            {otpBusy ? "Checking…" : "Verify code"}
                          </Button>
                        </DialogFooter>
                      </>
                    )}
                  </DialogContent>
                </Dialog>
              }
            </div>
            <VerifyRow icon={IdCard} label="ID" done={user.verified_id} onVerify={verifyId} testid="verify-id" />
          </div>
        </section>

        <section className="mt-6 p-6 bg-white border border-stone-200 rounded-2xl grid sm:grid-cols-2 gap-4">
          <F label="Name"><Input data-testid="prof-name" value={form.name || ""} onChange={(e) => upd("name", e.target.value)} /></F>
          <F label="Phone"><Input data-testid="prof-phone" value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></F>
          <F label="Role">
            <Select value={form.role || "seeker"} onValueChange={(v) => upd("role", v)}>
              <SelectTrigger data-testid="prof-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="seeker">Seeker</SelectItem><SelectItem value="lister">Lister</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Gender">
            <Select value={form.gender || "female"} onValueChange={(v) => upd("gender", v)}>
              <SelectTrigger data-testid="prof-gender"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Age"><Input data-testid="prof-age" type="number" value={form.age || ""} onChange={(e) => upd("age", Number(e.target.value))} /></F>
          <F label="Occupation"><Input data-testid="prof-occupation" value={form.occupation || ""} onChange={(e) => upd("occupation", e.target.value)} /></F>
          <F label="Food"><Select value={form.food_pref || "any"} onValueChange={(v) => upd("food_pref", v)}>
            <SelectTrigger data-testid="prof-food"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="veg">Veg</SelectItem><SelectItem value="eggetarian">Eggetarian</SelectItem></SelectContent>
          </Select></F>
          <F label="City"><Input data-testid="prof-city" value={form.city || ""} placeholder="e.g. Mumbai, New York" onChange={(e) => upd("city", e.target.value)} /></F>
          <F label="Bio" span><Textarea data-testid="prof-bio" rows={3} value={form.bio || ""} onChange={(e) => upd("bio", e.target.value)} /></F>
          <div className="sm:col-span-2 flex justify-end">
            <Button data-testid="prof-save" className="brand-accent rounded-full" onClick={save}>Save profile</Button>
          </div>
        </section>
      </div>
    </div>
  );
}
const F = ({ label, children, span }) => <div className={span ? "sm:col-span-2" : ""}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
const VerifyRow = ({ icon: Icon, label, done, onVerify, testid }) => (
  <div className="p-4 border border-stone-200 rounded-xl flex items-center gap-3">
    <Icon className="w-5 h-5 text-brand" />
    <div className="flex-1">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-stone-500">{done ? "Verified" : "Not verified"}</div>
    </div>
    {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> :
      onVerify && <Button size="sm" variant="outline" data-testid={testid} className="rounded-full" onClick={onVerify}>Verify</Button>}
  </div>
);
